import { useState, useEffect, useCallback } from 'react';
import { Hash, Search, Plus, RefreshCw, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { taxApi } from '@/api/tax.api';
import type { TaxCode, TaxCodeGlMapping, TaxJurisdiction, TaxType } from '@/api/tax.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { Combobox } from '@/components/ui/Combobox';
import { cn } from '@/utils/cn';

const ROUNDING_METHODS = ['None', 'Up', 'Down', 'Nearest'];

// ── GL Mapping Edit Dialog ────────────────────────────────────────────────────

interface GlMappingDialogProps {
  open: boolean;
  taxCodeId: string;
  mapping: TaxCodeGlMapping | null;
  onClose: () => void;
  onSaved: () => void;
}

const GlMappingDialog = ({ open, taxCodeId, mapping, onClose, onSaved }: GlMappingDialogProps) => {
  const [form, setForm] = useState<Partial<TaxCodeGlMapping>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(mapping ? { ...mapping } : {
      inputTaxAccountCode: null, outputTaxAccountCode: null,
      taxPayableAccountCode: null, taxReceivableAccountCode: null,
      withholdingTaxAccountCode: null, deferredTaxAccountCode: null,
      withholdingTaxReceivableAccountCode: null,
    });
    setError(null);
  }, [mapping, open]);

  if (!open) return null;

  const set = (k: keyof TaxCodeGlMapping, v: string | null) => setForm(f => ({ ...f, [k]: v || null }));

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await taxApi.updateGlMapping(taxCodeId, form);
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-9 px-3 rounded-md border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500';
  const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  const fields: { k: keyof TaxCodeGlMapping; label: string }[] = [
    { k: 'inputTaxAccountCode', label: 'Input Tax Account' },
    { k: 'outputTaxAccountCode', label: 'Output Tax Account' },
    { k: 'taxPayableAccountCode', label: 'Tax Payable Account' },
    { k: 'taxReceivableAccountCode', label: 'Tax Receivable Account' },
    { k: 'withholdingTaxAccountCode', label: 'Withholding Tax Payable (AP)' },
    { k: 'withholdingTaxReceivableAccountCode', label: 'Withholding Tax Receivable (AR)' },
    { k: 'deferredTaxAccountCode', label: 'Deferred Tax Account' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">GL Mapping</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {error && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">{error}</div>}
          {fields.map(({ k, label }) => (
            <div key={k}>
              <label className={labelCls}>{label}</label>
              <input className={inputCls} value={(form[k] as string) ?? ''} onChange={e => set(k, e.target.value)} maxLength={50} placeholder="Account code" />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="h-8 px-4 text-xs rounded-md bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
            {saving && <Spinner size="sm" />} Save GL Mapping
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Code Dialog ───────────────────────────────────────────────────────────────

interface CodeDialogProps {
  open: boolean;
  record: TaxCode | null;
  jurisdictions: TaxJurisdiction[];
  taxTypes: TaxType[];
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_CODE: Partial<TaxCode> = {
  code: '', name: '', jurisdictionId: '', taxTypeId: '', taxRate: 0,
  effectiveFrom: new Date().toISOString().slice(0, 10), effectiveTo: null,
  isCompound: false, recoverablePercent: 100, minThreshold: null, maxThreshold: null,
  roundingMethod: 'Nearest', isInclusive: false, isActive: true, remarks: null,
};

const CodeDialog = ({ open, record, jurisdictions, taxTypes, onClose, onSaved }: CodeDialogProps) => {
  const [form, setForm] = useState<Partial<TaxCode>>(EMPTY_CODE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setForm(record ? { ...record } : { ...EMPTY_CODE }); setError(null); }, [record, open]);

  if (!open) return null;

  const set = (k: keyof TaxCode, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.code?.trim() || !form.name?.trim() || !form.jurisdictionId || !form.taxTypeId || !form.effectiveFrom) {
      setError('Code, Name, Jurisdiction, Tax Type and Effective From are required.'); return;
    }
    setSaving(true); setError(null);
    try {
      if (record) { await taxApi.updateCode(record.id, form); }
      else { await taxApi.createCode(form); }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-9 px-3 rounded-md border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500';
  const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">{record ? 'Edit Tax Code' : 'New Tax Code'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          {error && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Code *</label>
              <input className={inputCls} value={form.code ?? ''} onChange={e => set('code', e.target.value)} maxLength={30} />
            </div>
            <div>
              <label className={labelCls}>Tax Rate (%) *</label>
              <input type="number" step="0.0001" className={inputCls} value={form.taxRate ?? 0} onChange={e => set('taxRate', parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Name *</label>
            <input className={inputCls} value={form.name ?? ''} onChange={e => set('name', e.target.value)} maxLength={150} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Jurisdiction *</label>
              <Combobox value={form.jurisdictionId ?? ''} onChange={v => set('jurisdictionId', v)}
                placeholder="Select…"
                options={jurisdictions.map(j => ({ value: j.id, label: `${j.jurisdictionCode} — ${j.jurisdictionName}` }))} />
            </div>
            <div>
              <label className={labelCls}>Tax Type *</label>
              <Combobox value={form.taxTypeId ?? ''} onChange={v => set('taxTypeId', v)}
                placeholder="Select…"
                options={taxTypes.map(t => ({ value: t.id, label: `${t.typeCode} — ${t.typeName}` }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Effective From *</label>
              <input type="date" className={inputCls} value={form.effectiveFrom ?? ''} onChange={e => set('effectiveFrom', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Effective To</label>
              <input type="date" className={inputCls} value={form.effectiveTo ?? ''} onChange={e => set('effectiveTo', e.target.value || null)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Recoverable %</label>
              <input type="number" step="0.01" min="0" max="100" className={inputCls} value={form.recoverablePercent ?? 100} onChange={e => set('recoverablePercent', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <label className={labelCls}>Min Threshold</label>
              <input type="number" step="0.01" className={inputCls} value={form.minThreshold ?? ''} onChange={e => set('minThreshold', e.target.value ? parseFloat(e.target.value) : null)} />
            </div>
            <div>
              <label className={labelCls}>Max Threshold</label>
              <input type="number" step="0.01" className={inputCls} value={form.maxThreshold ?? ''} onChange={e => set('maxThreshold', e.target.value ? parseFloat(e.target.value) : null)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Rounding Method</label>
            <Combobox value={form.roundingMethod ?? 'Nearest'} onChange={v => set('roundingMethod', v)} clearable={false}
              options={ROUNDING_METHODS.map(r => ({ value: r, label: r }))} />
          </div>
          <div className="flex items-center gap-4">
            {(['isInclusive', 'isCompound', 'isActive'] as const).map(k => (
              <label key={k} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} />
                {k === 'isInclusive' ? 'Inclusive' : k === 'isCompound' ? 'Compound' : 'Active'}
              </label>
            ))}
          </div>
          <div>
            <label className={labelCls}>Remarks</label>
            <textarea className="w-full px-3 py-2 rounded-md border text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
              rows={2} value={form.remarks ?? ''} onChange={e => set('remarks', e.target.value || null)} maxLength={500} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="h-8 px-4 text-xs rounded-md bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
            {saving && <Spinner size="sm" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const TaxCodesPage = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.TAX_CODE_CREATE as string);
  const canEdit   = hasPermission(Permissions.TAX_CODE_EDIT as string);
  const canDelete = hasPermission(Permissions.TAX_CODE_DELETE as string);

  const [items,   setItems]   = useState<TaxCode[]>([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [search,      setSearch]      = useState('');
  const [selected,    setSelected]    = useState<TaxCode | null>(null);
  const [detailTab,   setDetailTab]   = useState<'details' | 'gl'>('details');
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [editRecord,  setEditRecord]  = useState<TaxCode | null>(null);
  const [glDialogOpen, setGlDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TaxCode | null>(null);
  const [deleting,    setDeleting]    = useState(false);

  // Dropdown data
  const [jurisdictions, setJurisdictions] = useState<TaxJurisdiction[]>([]);
  const [taxTypes,      setTaxTypes]      = useState<TaxType[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taxApi.getCodes({ search: search || undefined, pageNumber: page, pageSize });
      const d = res.data?.data;
      setItems(d?.items ?? []);
      setTotal(d?.totalCount ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    taxApi.getJurisdictions({ pageSize: 500 }).then(r => setJurisdictions(r.data?.data?.items ?? [])).catch(() => {});
    taxApi.getTypes({ pageSize: 500 }).then(r => setTaxTypes(r.data?.data?.items ?? [])).catch(() => {});
  }, []);

  const handleSaved = () => { setDialogOpen(false); setEditRecord(null); load(); };
  const handleGlSaved = () => { setGlDialogOpen(false); if (selected) load(); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await taxApi.deleteCode(deleteTarget.id); setDeleteTarget(null); if (selected?.id === deleteTarget.id) setSelected(null); load(); }
    catch { /* silent */ }
    finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / pageSize);
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtRate = (r: number) => `${r.toFixed(2)}%`;

  const glFields: { k: keyof TaxCodeGlMapping; label: string }[] = [
    { k: 'inputTaxAccountCode', label: 'Input Tax Account' },
    { k: 'outputTaxAccountCode', label: 'Output Tax Account' },
    { k: 'taxPayableAccountCode', label: 'Tax Payable Account' },
    { k: 'taxReceivableAccountCode', label: 'Tax Receivable Account' },
    { k: 'withholdingTaxAccountCode', label: 'Withholding Tax Payable (AP)' },
    { k: 'withholdingTaxReceivableAccountCode', label: 'Withholding Tax Receivable (AR)' },
    { k: 'deferredTaxAccountCode', label: 'Deferred Tax Account' },
  ];

  return (
    <div className="flex h-full bg-slate-50 dark:bg-slate-900">
      {/* Left — list */}
      <div className="flex flex-col w-1/2 border-r border-slate-200 dark:border-slate-700">
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Hash size={18} className="text-brand-600" /> Tax Codes
            </h1>
            <div className="flex items-center gap-1">
              <button onClick={load} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400"><RefreshCw size={14} /></button>
              {canCreate && (
                <button onClick={() => { setEditRecord(null); setDialogOpen(true); }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded-lg transition-colors">
                  <Plus size={13} /> New
                </button>
              )}
            </div>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search code or name…"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40"><Spinner size="lg" /></div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
                <tr>
                  {['Code', 'Name', 'Rate', 'Incl.', 'Status'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map(row => (
                  <tr key={row.id}
                    onClick={() => { setSelected(row); setDetailTab('details'); }}
                    className={cn('cursor-pointer transition-colors', selected?.id === row.id ? 'bg-brand-50 dark:bg-brand-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50')}>
                    <td className="px-3 py-2 font-mono font-semibold text-brand-700 dark:text-brand-400">{row.code}</td>
                    <td className="px-3 py-2 text-slate-800 dark:text-slate-200 truncate max-w-[120px]">{row.name}</td>
                    <td className="px-3 py-2 font-mono text-slate-600 dark:text-slate-400">{fmtRate(row.taxRate)}</td>
                    <td className="px-3 py-2">
                      {row.isInclusive && <span className="px-1.5 py-0.5 rounded text-[9px] bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">Incl</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn('px-1.5 py-0.5 rounded-full text-[9px] font-semibold',
                        row.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                        {row.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2">
            <p className="text-[10px] text-slate-500 dark:text-gray-400">Page {page}/{totalPages} · {total} records</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronLeft size={14} /></button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="p-1 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Right — detail */}
      <div className="flex flex-col flex-1 min-w-0">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <Hash size={40} className="mb-3 opacity-30" />
            <p className="text-sm">Select a tax code to view details</p>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-5 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Tax Code</p>
                  <h2 className="text-base font-bold text-slate-900 dark:text-white font-mono">{selected.code} — {selected.name}</h2>
                </div>
                <div className="flex items-center gap-1">
                  {canEdit && <button onClick={() => { setEditRecord(selected); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-500"><Edit2 size={14} /></button>}
                  {canDelete && <button onClick={() => setDeleteTarget(selected)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>}
                </div>
              </div>
              <div className="flex gap-0 mt-3">
                {(['details', 'gl'] as const).map(tab => (
                  <button key={tab} onClick={() => setDetailTab(tab)}
                    className={cn('px-4 py-2 text-xs font-medium border-b-2 transition-colors',
                      detailTab === tab ? 'border-brand-500 text-brand-600' : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700')}>
                    {tab === 'details' ? 'Details' : 'GL Mapping'}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-auto p-5">
              {detailTab === 'details' && (
                <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-xs max-w-2xl">
                  {[
                    ['Code', selected.code], ['Name', selected.name],
                    ['Tax Rate', fmtRate(selected.taxRate)],
                    ['Recoverable %', `${selected.recoverablePercent}%`],
                    ['Rounding', selected.roundingMethod],
                    ['Effective From', fmtDate(selected.effectiveFrom)],
                    ['Effective To', selected.effectiveTo ? fmtDate(selected.effectiveTo) : '—'],
                    ['Min Threshold', selected.minThreshold != null ? selected.minThreshold.toFixed(2) : '—'],
                    ['Max Threshold', selected.maxThreshold != null ? selected.maxThreshold.toFixed(2) : '—'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                      <span className="text-slate-400 text-[10px] uppercase tracking-wide">{k}</span>
                      <span className="text-slate-800 dark:text-slate-200 font-medium mt-0.5">{v}</span>
                    </div>
                  ))}
                  <div className="col-span-2 flex items-center gap-4">
                    {[['Inclusive', selected.isInclusive], ['Compound', selected.isCompound], ['Active', selected.isActive]].map(([k, v]) => (
                      <span key={String(k)} className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
                        v ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                        {String(k)}: {v ? 'Yes' : 'No'}
                      </span>
                    ))}
                  </div>
                  {selected.remarks && (
                    <div className="col-span-2">
                      <span className="text-slate-400 text-[10px] uppercase tracking-wide">Remarks</span>
                      <p className="text-slate-700 dark:text-slate-300 mt-0.5">{selected.remarks}</p>
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'gl' && (
                <div className="max-w-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">GL Account Mappings</h3>
                    {canEdit && (
                      <button onClick={() => setGlDialogOpen(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-brand-500 text-brand-600 rounded hover:bg-brand-50 transition-colors">
                        <Edit2 size={12} /> Edit GL Mapping
                      </button>
                    )}
                  </div>
                  {glFields.map(({ k, label }) => (
                    <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                      <span className="text-xs text-slate-500 dark:text-gray-400">{label}</span>
                      <span className={cn('text-xs font-mono font-medium',
                        selected.glMapping?.[k] ? 'text-slate-800 dark:text-slate-200' : 'text-slate-300 dark:text-slate-600')}>
                        {(selected.glMapping?.[k] as string | null | undefined) ?? '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <CodeDialog open={dialogOpen} record={editRecord} jurisdictions={jurisdictions} taxTypes={taxTypes}
        onClose={() => { setDialogOpen(false); setEditRecord(null); }} onSaved={handleSaved} />

      {selected && (
        <GlMappingDialog open={glDialogOpen} taxCodeId={selected.id} mapping={selected.glMapping}
          onClose={() => setGlDialogOpen(false)} onSaved={handleGlSaved} />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Delete Tax Code?</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Delete <strong>{deleteTarget.code}</strong>? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="h-8 px-4 text-xs rounded-md bg-red-600 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
                {deleting && <Spinner size="sm" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
